import { useState } from "react";
import { Droplets, Sunrise, Sunset, Wind } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGet } from "@/lib/api";
import { beaufort, fmt, weatherInfo, windDir, atPlace } from "@/lib/meta";

const TownTile = ({ t, active, onClick }) => {
  const { label, Icon } = weatherInfo(t.current?.weather_code);
  return (
    <button onClick={onClick} data-testid={`weather-town-${t.town}`}
      className={`flex h-full flex-col items-start justify-between border p-5 text-left transition-colors duration-200 ${active ? "border-ocean bg-ocean text-shell" : "border-foreground/15 bg-white/50 hover:bg-white"}`}>
      <span className="eyebrow">{t.town}</span>
      <div className="mt-3 flex items-center gap-3">
        <Icon size={28} strokeWidth={1.25} />
        <span className="font-display text-4xl">{Math.round(t.current?.temperature_2m)}°</span>
      </div>
      <span className="mt-2 text-sm opacity-80">{label}</span>
      <span className="mt-1 flex items-center gap-1 text-xs opacity-70">
        <Wind size={12} /> {windDir(t.current?.wind_direction_10m)} {beaufort(t.current?.wind_speed_10m)} Bft
      </span>
    </button>
  );
};

const Forecast = ({ t }) => {
  const d = t.daily;
  return (
    <div className="grid grid-cols-1 gap-px border border-foreground/15 bg-foreground/15 md:grid-cols-[1.2fr_repeat(5,1fr)]" data-testid="weather-forecast">
      <div className="bg-shell p-5">
        <p className="eyebrow text-muted-foreground">Jetzt {atPlace(t.town)}</p>
        <p className="mt-2 text-sm">Gefühlt {Math.round(t.current.apparent_temperature)}° · Böen {Math.round(t.current.wind_gusts_10m)} km/h</p>
        <p className="mt-1 flex items-center gap-1.5 text-sm"><Droplets size={14} /> {t.current.relative_humidity_2m}% Luftfeuchte</p>
        <p className="mt-1 flex items-center gap-1.5 text-sm"><Sunrise size={14} /> {fmt(d.sunrise[0], "HH:mm")} <Sunset size={14} className="ml-2" /> {fmt(d.sunset[0], "HH:mm")}</p>
      </div>
      {d.time.map((day, i) => {
        const { label, Icon } = weatherInfo(d.weather_code[i]);
        return (
          <div key={day} className="bg-shell p-5" data-testid={`forecast-day-${i}`}>
            <p className="eyebrow">{i === 0 ? "Heute" : fmt(day, "EEE d.")}</p>
            <Icon className="mt-3" size={22} strokeWidth={1.25} />
            <p className="mt-2 font-sub text-lg">{Math.round(d.temperature_2m_max[i])}° <span className="text-muted-foreground">/ {Math.round(d.temperature_2m_min[i])}°</span></p>
            <p className="text-xs text-muted-foreground">{label} · {d.precipitation_probability_max[i] ?? 0}% Regen</p>
          </div>
        );
      })}
    </div>
  );
};

export const WeatherWidget = ({ compact = false, sel: selProp, setSel: setSelProp }) => {
  const { data, isLoading, isError } = useGet("weather", "/weather");
  const [selLocal, setSelLocal] = useState(0);
  const sel = selProp ?? selLocal;
  const setSel = setSelProp ?? setSelLocal;
  if (isLoading) return <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-36" />)}</div>;
  if (isError) return <p className="text-sm text-muted-foreground" data-testid="weather-error">Wetterdaten sind gerade nicht verfügbar.</p>;
  const towns = compact ? data.slice(0, 4) : data;
  return (
    <div className={compact ? "h-full" : "space-y-6"} data-testid="weather-widget">
      <div className={`grid grid-cols-2 gap-4 ${compact ? "h-full auto-rows-fr" : "md:grid-cols-4 xl:grid-cols-8"}`}>
        {towns.map((t, i) => <TownTile key={t.town} t={t} active={!compact && i === sel} onClick={() => setSel(i)} />)}
      </div>
      {!compact && <Forecast t={data[sel]} />}
    </div>
  );
};
